import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Image as ImageIcon, Lock, Plus, PanelLeftOpen, PanelRightOpen } from 'lucide-react';

import CanvasStage, { type BoxRenderMode } from '../components/canvas/CanvasStage';
import { type RightRailView } from '../components/canvas/RightRail';
import ImageMetaOverlay from '../components/canvas/ImageMetaOverlay';
import ImageNavControls from '../components/canvas/ImageNavControls';
import ModifiersHint from '../components/canvas/ModifiersHint';
import RenderModeToggle from '../components/canvas/RenderModeToggle';
import ZoomControls from '../components/canvas/ZoomControls';
import ImageGridSidebar from '../components/sidebar/ImageGridSidebar';
import AboutModal from '../components/AboutModal';
import LicenseModal from '../components/LicenseModal';
import AddClassModal from '../components/AddClassModal';
import EditClassModal from '../components/EditClassModal';
import ImageUploadModal from '../components/ImageUploadModal';
import LockAndSnapshotModal from '../components/LockAndSnapshotModal';
import DownloadImagesModal from '../components/DownloadImagesModal';
import DeleteImageModal from '../components/DeleteImageModal';
import BulkDeleteImagesModal from '../components/BulkDeleteImagesModal';
import ExportDatasetModal from '../components/ExportDatasetModal';
import ClassPickerModal from '../components/ClassPickerModal';
import SplitChip from '../components/SplitChip';
import SplitDetailsModal from '../components/SplitDetailsModal';
import RenameImageModal from '../components/RenameImageModal';
import toast from 'react-hot-toast';
import { imagesApi, snapshotsApi } from '../services/annotatorApi';
import { useOrgConfig } from '../components/ConfigContext';
import type { ImageMeta, SplitSummary } from '../types/api';
import BoxContextMenu from '../components/canvas/BoxContextMenu';
import RightRail from '../components/canvas/RightRail';
import AnnotatorHeader from '../components/AnnotatorHeader';
import DeleteClassModal from '../components/DeleteClassModal';
import { Button } from '../components/ui';
import { useAnnotatorState } from '../hooks/useAnnotatorState';
import { useDarkMode } from '../hooks/useDarkMode';
import { cn } from '../lib/cn';
import type { Class as ClassModel } from '../types/api';

const IGNODE_MODE = import.meta.env.VITE_IGNODE_MODE === 'true';
const PORTAL_URL = import.meta.env.VITE_IGNODE_PORTAL_URL ?? '';

interface ContextMenuState {
    boxId: string;
    x: number;
    y: number;
}

interface DeleteClassState {
    cls: ClassModel;
    inUseCount: number;
}

/**
 * Annotator — focused workspace per PIVOTS_2026_05_31.md.
 *
 * This view file deliberately stays orchestration-heavy: state, effects,
 * event wiring. Header + delete-class modal + canvas overlays + right rail
 * live in their own component files under components/. Keep this under 300
 * lines per the "keep files small" rule.
 */
export default function Annotator() {
    const { t: tCommon } = useTranslation('common');
    const { t: tCanvas } = useTranslation('canvas');
    const { orgId, projectId } = useParams<{ orgId: string; projectId: string }>();
    const navigate = useNavigate();

    const { setTheme, isDarkMode } = useDarkMode();
    const state = useAnnotatorState({ orgId, projectId });
    // D.2.7 — effective per-org quotas (per-org override → platform default
    // → env fallback). Drives client-side coaching like the upload size cap.
    const { config: orgConfig } = useOrgConfig(orgId);

    const [aboutOpen, setAboutOpen] = useState(false);
    const [licenseOpen, setLicenseOpen] = useState(false);
    const [addClassOpen, setAddClassOpen] = useState(false);
    const [editClass, setEditClass] = useState<ClassModel | null>(null);
    // Download modal target: either ONE image (single mode) or ALL of them
    // (bulk mode). The shared DownloadImagesModal branches on array length.
    const [downloadTarget, setDownloadTarget] = useState<ImageMeta[] | null>(null);
    const [deleteImage, setDeleteImage] = useState<ImageMeta | null>(null);
    // D.14 — bulk-delete confirm modal. Holds the IDs the sidebar emitted
    // until the user confirms; on success we prune them from the in-
    // memory image list.
    const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([]);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [lockModalOpen, setLockModalOpen] = useState(false);
    // G.6 — persisted split summary for the locked project. Populated
    // when (a) the lock modal returns from a successful lock and hands
    // us split_summary in its onResult, or (b) the customer opens a
    // project that's already locked — fetched lazily from /split-summary.
    const [splitSummary, setSplitSummary] = useState<SplitSummary | null>(null);
    const [splitDetailsOpen, setSplitDetailsOpen] = useState(false);
    // Image rename — opened via the pencil icon on the canvas
    // ImageMetaOverlay (rendered only when project is unlocked).
    const [renameTarget, setRenameTarget] = useState<ImageMeta | null>(null);
    // Track the latest snapshot id while the project is locked so the
    // Export modal can fetch the split summary on its own (it stays
    // presentational — Annotator owns the id, modal owns the call).
    const [lockedSnapshotId, setLockedSnapshotId] = useState<string | null>(null);
    // Collapsible sidebars — toggleable at ALL widths (J.15 — responsive +
    // user request: even on wide screens, give the customer the option to
    // collapse a rail for more canvas room).
    //
    // Default: open on ≥lg (1024px+), closed below. On lg+ both can be open
    // simultaneously (docked layout has room for both). Below lg they're
    // mutually exclusive — opening one closes the other (drawer-style).
    const [leftSidebarOpen, setLeftSidebarOpen] = useState<boolean>(() =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(min-width: 1024px)').matches
            : true,
    );
    const [rightRailOpen, setRightRailOpen] = useState<boolean>(() =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(min-width: 1024px)').matches
            : true,
    );
    // Track whether we're below lg so helpers can branch reactively
    // (mutual-exclusion only fires on small screens). Initial value
    // safe for SSR.
    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(() =>
        typeof window !== 'undefined' && window.matchMedia
            ? window.matchMedia('(max-width: 1023.98px)').matches
            : false,
    );
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(max-width: 1023.98px)');
        const onChange = (e: MediaQueryListEvent) => {
            setIsSmallScreen(e.matches);
            if (e.matches) {
                // Crossed lg → sub-lg: minimize both. User can re-open
                // explicitly via the edge-tab buttons.
                setLeftSidebarOpen(false);
                setRightRailOpen(false);
            }
        };
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);
    // Helpers — mutual-exclusion is scoped to small screens because at
    // lg+ both rails CAN be docked side-by-side. At lg+ opening one
    // leaves the other alone (the user clearly has room for both).
    const openLeft = () => {
        setLeftSidebarOpen(true);
        if (isSmallScreen) setRightRailOpen(false);
    };
    const openRight = () => {
        setRightRailOpen(true);
        if (isSmallScreen) setLeftSidebarOpen(false);
    };
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [classFilterModalOpen, setClassFilterModalOpen] = useState(false);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [deleteClass, setDeleteClass] = useState<DeleteClassState | null>(null);
    const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);

    // Zoom / pan transform — lifted from CanvasStage so ZoomControls + wheel
    // both drive the same state. Reset whenever the active image changes.
    const [transform, setTransform] = useState({ scale: 1, offsetX: 0, offsetY: 0 });
    useEffect(() => {
        setTransform({ scale: 1, offsetX: 0, offsetY: 0 });
    }, [state.activeImageId]);

    // Pan-tool mode toggle (Hand icon in ZoomControls). When ON, click-drag
    // on the canvas pans instead of starting a new box. Auto-exits on Esc.
    const [panMode, setPanMode] = useState(false);

    // Draw-priority mode — clicking the My Canvas thumbnail flips this on
    // so the user can draw a new box anywhere (including inside an existing
    // one) without holding Shift. Mutually exclusive with panMode.
    const [drawPriorityMode, setDrawPriorityMode] = useState(false);

    // Right-rail view + status filter persisted in URL search params
    // (`?view=classes|canvas|properties&filter=pending|annotated|flagged`).
    // Survives reload, bookmarkable, and browser back/forward replays the
    // exact workspace state. We use replace:true on every set so toggling
    // panels doesn't pollute the history stack.
    //
    // Auto-switch behaviour (unchanged from when state was local):
    //   • Canvas click on a box        → setView('properties')  (Edit flow)
    //   • List row click in My Canvas  → no change               (Browse flow)
    //   • List row Sliders icon        → setView('properties')
    //   • Context menu Open-in-Props   → setView('properties')
    //   • Right-click anywhere         → no view change
    // URL value `?view=canvas` maps to internal RightRailView value 'boxes'
    // — the customer-facing label is "My Canvas" so we publish `canvas` in
    // the URL, but the type's tag is the internal name. Mapping is local
    // to this view; rest of the app keeps using the internal name.
    const [searchParams, setSearchParams] = useSearchParams();
    const rawView = searchParams.get('view');
    const rightRailView: RightRailView =
        rawView === 'canvas' ? 'boxes'
            : rawView === 'properties' ? 'properties'
                : 'classes';
    const setRightRailView = (next: RightRailView) => {
        setSearchParams((prev) => {
            const out = new URLSearchParams(prev);
            if (next === 'classes') out.delete('view');
            else if (next === 'boxes') out.set('view', 'canvas');
            else out.set('view', next);
            return out;
        }, { replace: true });
    };
    // Filter param mirrors useAnnotatorState's `statusFilter`. Wired below
    // by passing rawFilter into setStatusFilter on mount + writing back
    // here on filter change.
    const rawFilter = searchParams.get('filter');
    const filterFromUrl = (rawFilter === 'pending' || rawFilter === 'annotated' || rawFilter === 'flagged')
        ? rawFilter
        : '';
    useEffect(() => {
        if (filterFromUrl !== state.statusFilter) {
            state.setStatusFilter(filterFromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterFromUrl]);
    const setStatusFilterAndSyncUrl = (next: typeof state.statusFilter) => {
        state.setStatusFilter(next);
        setSearchParams((prev) => {
            const out = new URLSearchParams(prev);
            if (!next) out.delete('filter');
            else out.set('filter', next);
            return out;
        }, { replace: true });
    };
    // When selection clears, return to Classes (mirrors the prior behavior).
    useEffect(() => {
        if (selectedBox === null && rightRailView === 'properties') {
            setRightRailView('classes');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const openBoxInProperties = (id: string) => {
        state.setSelectedBoxId(id);
        setRightRailView('properties');
        setDrawPriorityMode(false);
    };
    const togglePanMode = () => {
        setPanMode((v) => !v);
        if (!panMode) setDrawPriorityMode(false);
    };
    const toggleDrawPriority = () => {
        setDrawPriorityMode((v) => !v);
        if (!drawPriorityMode) setPanMode(false);
    };

    // Box rendering mode (outline / shaded / both) — persisted in localStorage.
    const [renderMode, setRenderMode] = useState<BoxRenderMode>(() => {
        try {
            const stored = localStorage.getItem('ignode-annotator-render-mode');
            if (stored === 'outline' || stored === 'shaded' || stored === 'both') return stored;
        } catch { /* private mode */ }
        return 'both';
    });
    useEffect(() => {
        try { localStorage.setItem('ignode-annotator-render-mode', renderMode); } catch { /* ignore */ }
    }, [renderMode]);

    // Per-class visibility — persisted per-project so re-entering restores focus state.
    const visibilityKey = `ignode-annotator-hidden-classes:${projectId ?? ''}`;
    const [hiddenClassIndices, setHiddenClassIndices] = useState<Set<number>>(() => {
        try {
            const stored = localStorage.getItem(visibilityKey);
            if (stored) return new Set(JSON.parse(stored) as number[]);
        } catch { /* ignore */ }
        return new Set();
    });
    useEffect(() => {
        try { localStorage.setItem(visibilityKey, JSON.stringify(Array.from(hiddenClassIndices))); } catch { /* ignore */ }
    }, [hiddenClassIndices, visibilityKey]);
    const toggleClassVisibility = (classIndex: number) => {
        setHiddenClassIndices((prev) => {
            const next = new Set(prev);
            if (next.has(classIndex)) next.delete(classIndex);
            else next.add(classIndex);
            return next;
        });
    };

    // Canvas viewport sizing.
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });
    useLayoutEffect(() => {
        const el = canvasContainerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            setCanvasSize({ w: el.clientWidth, h: el.clientHeight });
        });
        ro.observe(el);
        setCanvasSize({ w: el.clientWidth, h: el.clientHeight });
        return () => ro.disconnect();
    }, [state.activeImageId]);

    // Keyboard shortcuts.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }
            const mod = e.ctrlKey || e.metaKey;
            if (mod && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) state.redo();
                else state.undo();
                return;
            }
            // Ctrl+S — flush all pending debounced saves immediately.
            if (mod && e.key.toLowerCase() === 's') {
                e.preventDefault();
                state.flushPendingSaves();
                return;
            }
            if (e.key === 'd' || e.key === 'Delete' || e.key === 'Backspace') {
                if (state.selectedBoxIds.size > 0) {
                    state.deleteSelected();
                    e.preventDefault();
                } else if (state.selectedBoxId) {
                    state.deleteBox(state.selectedBoxId);
                    e.preventDefault();
                }
            } else if (e.key === 'Escape') {
                state.setSelectedBoxId(null);
                setContextMenu(null);
                setPanMode(false);
                setDrawPriorityMode(false);
            } else if (e.key === 'Enter' && !e.shiftKey) {
                state.navigateImage(1);
            } else if (e.key === 'Enter' && e.shiftKey) {
                state.navigateImage(-1);
            } else if (e.key >= '1' && e.key <= '9') {
                const idx = parseInt(e.key, 10) - 1;
                const cls = state.classes[idx];
                if (cls) state.setActiveClassIndex(cls.class_index);
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                // Two-mode disambiguation:
                //   • box selected  → fall through to the nudge branch below
                //   • no selection  → navigate the image list. ↑ = previous,
                //     ↓ = next. Same primitive as Enter/Shift+Enter.
                if (!state.selectedBoxId) {
                    e.preventDefault();
                    state.navigateImage(e.key === 'ArrowDown' ? 1 : -1);
                    return;
                }
                // selected — fall through to nudge logic below.
            }
            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                // Nudge the selected box. Single arrow ≈ 5 viewport pixels;
                // Shift+Arrow ≈ 25 viewport pixels (coarse positioning).
                //
                // Why we scale by the rendered size, not the natural image
                // size: native-pixel steps look invisible at fit-to-screen
                // (a 1-px move on a 4000-px image rendered at 800-px viewport
                // is 0.2 screen pixels). Anchoring to viewport pixels keeps
                // the felt step constant regardless of zoom or image size.
                //
                // canvasSize.w is the rendered viewport width; transform.scale
                // is the user's current zoom on top of fit. So
                //   1 viewport px = naturalSize.w / canvasSize.w / scale
                //                 normalized units of x_center
                if (!state.selectedBoxId || !naturalSize || canvasSize.w <= 0 || canvasSize.h <= 0) return;
                const viewportStepPx = e.shiftKey ? 12.5 : 2.5;
                const dxNorm = (e.key === 'ArrowLeft' ? -viewportStepPx : e.key === 'ArrowRight' ? viewportStepPx : 0)
                    / canvasSize.w / transform.scale;
                const dyNorm = (e.key === 'ArrowUp' ? -viewportStepPx : e.key === 'ArrowDown' ? viewportStepPx : 0)
                    / canvasSize.h / transform.scale;
                if (dxNorm === 0 && dyNorm === 0) return;
                e.preventDefault();
                // nudgeBox reads boxesRef.current so auto-repeat compounds
                // correctly instead of stamping the same destination twice.
                state.nudgeBox(state.selectedBoxId, dxNorm, dyNorm);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [state]);

    const handleBack = () => {
        if (IGNODE_MODE) {
            // Portal opens the annotator in a new tab (LabelFactory.tsx →
            // window.open). "← Back" closes that tab, dropping the user
            // back into the Portal tab they came from. Browsers only
            // allow window.close() on tabs script-opened by JS; if the
            // user typed the URL directly (or refreshed and lost the
            // opener relationship in some browsers), close() is a no-op,
            // so we fall through to a Portal navigation as the safety
            // net.
            window.close();
            // If we're still here a tick later, close() refused — bounce
            // to Portal manually. setTimeout 0 gives the browser a chance
            // to actually close the tab before the redirect kicks in.
            window.setTimeout(() => {
                if (PORTAL_URL) {
                    window.location.href = `${PORTAL_URL}/dashboard/integrations/label-factory`;
                }
            }, 0);
            return;
        }
        if (orgId) {
            navigate(`/org/${orgId}`);
        }
    };

    const hasImages = state.images.length > 0;
    const hasClasses = state.classes.length > 0;
    const isLocked = state.project?.is_locked ?? false;
    const pendingImageCount = state.images.filter((i) => i.status === 'pending').length;

    // G.6 — lazy-fetch split summary on first lock-observed render. When
    // unlocked we drop the cached summary so the banner doesn't linger
    // after an unlock. When the lock modal hands back a summary directly,
    // this effect is a no-op (splitSummary is already set).
    useEffect(() => {
        let cancelled = false;
        if (!isLocked) {
            setSplitSummary(null);
            setLockedSnapshotId(null);
            return;
        }
        if (splitSummary || !projectId || !orgId) return;
        snapshotsApi.list(projectId, orgId)
            .then((snaps) => {
                if (cancelled || snaps.length === 0) return;
                setLockedSnapshotId(snaps[0].id);
                return snapshotsApi.splitSummary(snaps[0].id, orgId);
            })
            .then((sum) => {
                if (cancelled || !sum) return;
                setSplitSummary(sum);
            })
            .catch(() => {
                // Silent failure — banner just shows the brief "Locked
                // dataset" fallback instead of the chip strip.
            });
        return () => { cancelled = true; };
    }, [isLocked, projectId, orgId, splitSummary]);

    // Per-image download — pending images skip the modal (only one sensible
    // artifact) and download original directly. Annotated images open the
    // shared modal so the user picks raw vs. boxes-burnt-in PNG.
    const handleDownloadImage = (img: ImageMeta) => {
        if (img.status === 'annotated') {
            setDownloadTarget([img]);
        } else {
            window.location.href = imagesApi.bytesUrl(img.id, orgId);
        }
    };

    // G.7 — format + container come from the ExportDatasetModal's
    // radios. The actual download is a window.location redirect to the
    // backend's stream endpoint with both as query params.
    const downloadSnapshot = async (
        format: 'ignode' | 'yolo' | 'coco' = 'ignode',
        container: 'tar' | 'zip' = 'tar',
    ) => {
        if (!projectId) return;
        try {
            const snaps = await snapshotsApi.list(projectId, orgId);
            if (snaps.length === 0) {
                toast.error(tCanvas('no_snapshot_yet', { defaultValue: 'No snapshot yet — lock the project to create one.' }));
                return;
            }
            window.location.href = snapshotsApi.downloadUrl(snaps[0].id, orgId, { format, container });
        } catch (e) {
            toast.error(String((e as Error)?.message ?? e));
        }
    };
    const selectedBox = state.boxes.find((b) => b.id === state.selectedBoxId) ?? null;
    const contextMenuBox = contextMenu ? (state.boxes.find((b) => b.id === contextMenu.boxId) ?? null) : null;

    return (
        <div className={cn('flex flex-col h-screen w-full font-sans overflow-hidden', isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900')}>
            <AnnotatorHeader
                isDarkMode={isDarkMode}
                isIgnodeMode={IGNODE_MODE}
                projectName={state.project?.name}
                imageCount={state.images.length}
                classCount={state.classes.length}
                hasImages={hasImages}
                hasClasses={hasClasses}
                isLocked={isLocked}
                pendingCount={state.pendingCount}
                inFlightCount={state.inFlightCount}
                lastSavedAt={state.lastSavedAt}
                lastSaveError={state.lastSaveError}
                onFlushPending={state.flushPendingSaves}
                onSetTheme={setTheme}
                onBack={handleBack}
                onUpload={() => setUploadOpen(true)}
                onLockToggle={() => setLockModalOpen(true)}
                onDownloadSnapshot={() => setExportModalOpen(true)}
                onDownloadAllImages={hasImages ? () => setDownloadTarget(state.images) : undefined}
                onOpenAbout={() => setAboutOpen(true)}
                onOpenLicense={() => setLicenseOpen(true)}
            />

            {/* Body — left sidebar + canvas + right rail.
                Responsive behavior (J.15):
                  - ≥lg (1024px): both rails docked in-flow (w-80 each).
                  - <lg: rails become `position:fixed` slide-in drawers
                    that overlay the canvas; an edge "tab" button on
                    each side opens/closes them. Closed = canvas gets
                    the full viewport. */}
            <div className="flex-1 flex overflow-hidden relative">
                {/* Edge tab — visible at ALL widths whenever the left
                    sidebar is closed. Lets wide-screen users reclaim
                    canvas space too. */}
                {!leftSidebarOpen && (
                    <button
                        type="button"
                        onClick={openLeft}
                        title={tCommon('open_sidebar', { defaultValue: 'Open image list' })}
                        aria-label={tCommon('open_sidebar', { defaultValue: 'Open image list' })}
                        className={cn(
                            'absolute left-0 top-3 z-30 w-8 h-10 rounded-r-md border border-l-0 shadow-sm flex items-center justify-center transition-colors',
                            isDarkMode
                                ? 'bg-gray-800 border-gray-700 text-primary-400 hover:bg-gray-700'
                                : 'bg-white border-gray-300 text-primary-600 hover:bg-gray-50',
                        )}
                    >
                        <PanelLeftOpen className="h-4 w-4" />
                    </button>
                )}
                {/* Backdrop — closes both rails on tap. <lg only. */}
                {(leftSidebarOpen || rightRailOpen) && (
                    <div
                        onClick={() => { setLeftSidebarOpen(false); setRightRailOpen(false); }}
                        className="lg:hidden absolute inset-0 z-20 bg-black/30 backdrop-blur-[1px]"
                        aria-hidden="true"
                    />
                )}
                <aside
                    className={cn(
                        'border-r flex flex-col overflow-hidden transition-all duration-200',
                        'lg:relative lg:translate-x-0 lg:z-auto lg:shrink-0',
                        // Below lg, behave as a fixed slide-in drawer
                        'absolute inset-y-0 left-0 z-30 w-[85vw] max-w-sm',
                        leftSidebarOpen ? 'translate-x-0' : '-translate-x-full',
                        // Above lg, fixed 320px when open; collapses to 0
                        // width when closed so the canvas reclaims the
                        // space (no overlay, no transform shenanigans).
                        leftSidebarOpen ? 'lg:w-80' : 'lg:w-0',
                        isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white',
                    )}
                >
                    <ImageGridSidebar
                        onCollapse={() => setLeftSidebarOpen(false)}
                        isDarkMode={isDarkMode}
                        images={state.filteredImages}
                        activeImageId={state.activeImageId}
                        statusFilter={state.statusFilter}
                        orgId={orgId}
                        onSelectImage={state.setActiveImageId}
                        onChangeStatusFilter={(s) => { state.setClassFilterExclusive(null); setStatusFilterAndSyncUrl(s); }}
                        classes={state.classes}
                        classFilter={state.classFilter}
                        onOpenClassFilter={() => setClassFilterModalOpen(true)}
                        onClearClassFilter={() => state.setClassFilterExclusive(null)}
                        onDownloadImage={handleDownloadImage}
                        onDeleteImage={(img) => setDeleteImage(img)}
                        onDownloadAllImages={hasImages ? () => setDownloadTarget(state.images) : undefined}
                        onBulkDelete={(ids) => setBulkDeleteIds(ids)}
                        // G.6 (2026-06-07 UX revision) — sidebar banner
                        // removed; per-row chips stay. Banner moved to the
                        // top "Project is locked" strip in the canvas
                        // area, with a "View details" modal replacing the
                        // inline expansion that ate image-list space.
                        isLocked={isLocked}
                        splitFilter={state.splitFilter}
                        onChangeSplitFilter={(s) => state.setSplitFilter(s)}
                    />
                </aside>

                <main
                    ref={canvasContainerRef}
                    className={cn('flex-1 relative overflow-hidden', isDarkMode ? 'bg-gray-900' : 'bg-primary-50')}
                >
                    {state.loading && (
                        <div className={cn('absolute inset-0 grid place-items-center text-sm', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                            {tCommon('loading')}
                        </div>
                    )}
                    {state.error && (
                        <div className={cn('absolute top-3 left-3 right-3 z-30 rounded-lg border backdrop-blur-sm p-3 text-sm',
                            isDarkMode ? 'border-amber-800 bg-amber-900/30 text-amber-200' : 'border-amber-200 bg-amber-50/90 text-amber-900',
                        )}>
                            {state.error}
                        </div>
                    )}
                    {isLocked && (
                        <div className={cn(
                            'absolute top-3 left-3 right-3 z-30 rounded-lg border backdrop-blur-sm p-3 flex items-center gap-3 text-sm shadow-sm flex-wrap',
                            isDarkMode ? 'border-amber-800 bg-amber-900/40 text-amber-100' : 'border-amber-300 bg-amber-50/95 text-amber-900',
                        )}>
                            <Lock className="h-4 w-4 shrink-0" />
                            <span className="font-medium">{tCanvas('locked_banner')}</span>
                            {/* G.6 — inline split summary chips. Click "View
                                details" → full ClassCoveragePreview opens in
                                a modal. Replaces the sidebar banner-expansion
                                pattern which ate image-list space. */}
                            {splitSummary && (
                                <span className="ml-auto inline-flex items-center gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1">
                                        <SplitChip split="train" size="xs" isDarkMode={isDarkMode} />
                                        <span className="tabular-nums">{splitSummary.global.train}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <SplitChip split="val" size="xs" isDarkMode={isDarkMode} />
                                        <span className="tabular-nums">{splitSummary.global.val}</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                        <SplitChip split="test" size="xs" isDarkMode={isDarkMode} />
                                        <span className="tabular-nums">{splitSummary.global.test}</span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => setSplitDetailsOpen(true)}
                                        className={cn(
                                            'text-xs underline-offset-2 hover:underline',
                                            isDarkMode ? 'text-amber-200 hover:text-amber-100' : 'text-amber-800 hover:text-amber-900',
                                        )}
                                    >
                                        View details
                                    </button>
                                </span>
                            )}
                        </div>
                    )}
                    {!state.loading && state.activeImage && (
                        <>
                            <ModifiersHint isDarkMode={isDarkMode} />
                            <ImageMetaOverlay image={state.activeImage} />
                            <ZoomControls
                                isDarkMode={isDarkMode}
                                scale={transform.scale}
                                panMode={panMode}
                                onZoomIn={() => setTransform((t) => ({ ...t, scale: Math.min(t.scale * 1.2, 8) }))}
                                onZoomOut={() => setTransform((t) => ({ ...t, scale: Math.max(t.scale / 1.2, 0.2) }))}
                                onReset={() => setTransform({ scale: 1, offsetX: 0, offsetY: 0 })}
                                onTogglePanMode={togglePanMode}
                            />
                            {(panMode || drawPriorityMode) && (
                                <div
                                    className={cn(
                                        'absolute top-3 right-3 z-30 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold shadow-lg',
                                        isDarkMode
                                            ? 'bg-primary-500/20 text-primary-200 border border-primary-500/40'
                                            : 'bg-primary-100 text-primary-800 border border-primary-300',
                                    )}
                                >
                                    {panMode
                                        ? tCanvas('pan_active_hint', { defaultValue: '✋ Pan mode — drag to move · Esc to exit' })
                                        : tCanvas('draw_priority_canvas_hint', { defaultValue: '✏ Drawing on top — click anywhere to draw · Esc to exit' })}
                                </div>
                            )}
                            <RenderModeToggle isDarkMode={isDarkMode} value={renderMode} onChange={setRenderMode} />
                            <ImageNavControls
                                boxCount={state.boxes.length}
                                onPrev={() => state.navigateImage(-1)}
                                onNext={() => state.navigateImage(1)}
                            />
                            {canvasSize.w > 0 && canvasSize.h > 0 && (
                                <CanvasStage
                                    image={state.activeImage}
                                    boxes={state.boxes}
                                    classes={state.classes}
                                    activeClassIndex={state.activeClassIndex}
                                    selectedBoxId={state.selectedBoxId}
                                    orgId={orgId}
                                    width={canvasSize.w}
                                    height={canvasSize.h}
                                    transform={transform}
                                    setTransform={setTransform}
                                    panMode={panMode}
                                    drawPriorityMode={drawPriorityMode}
                                    isLocked={isLocked}
                                    renderMode={renderMode}
                                    hiddenClassIndices={hiddenClassIndices}
                                    onNaturalSize={setNaturalSize}
                                    onCreateBox={(d) => state.createBox(d)}
                                    onUpdateBox={(id, patch) => state.updateBox(id, patch)}
                                    onSelectBox={(id) => {
                                        // Canvas click on a box → select + auto-open Properties
                                        // (user clicked the visual thing they want to edit).
                                        state.setSelectedBoxId(id);
                                        if (id) setRightRailView('properties');
                                    }}
                                    onBoxContextMenu={(boxId, vx, vy) => {
                                        // Right-click on canvas → set selection for highlight,
                                        // surface menu, but do NOT switch view.
                                        state.setSelectedBoxId(boxId);
                                        setContextMenu({ boxId, x: vx, y: vy });
                                    }}
                                    onExitDrawPriority={() => setDrawPriorityMode(false)}
                                />
                            )}
                            {state.selectedBoxIds.size > 0 && (
                                <div className="absolute bottom-4 right-4 z-30 flex items-center gap-2 rounded-full bg-primary-600 text-white shadow-xl px-3 py-1.5 text-sm">
                                    <span className="font-medium">
                                        {tCanvas('selected_count', { count: state.selectedBoxIds.size })}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => state.deleteSelected()}
                                        className="ml-2 px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors duration-150"
                                    >
                                        {tCanvas('bulk_delete')}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* Empty states */}
                    {!state.loading && !state.activeImage && (
                        <div className="absolute inset-0 grid place-items-center px-6">
                            <div className="text-center max-w-md">
                                <div className={cn(
                                    'w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center',
                                    isDarkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-400',
                                )}>
                                    <ImageIcon className="h-7 w-7" />
                                </div>
                                <h2 className={cn('text-xl font-semibold', isDarkMode ? 'text-gray-100' : 'text-gray-900')}>
                                    {hasImages
                                        ? tCanvas('no_image_selected', { defaultValue: 'Select an image to start annotating' })
                                        : tCanvas('no_images_yet', { defaultValue: 'No images yet' })}
                                </h2>
                                <p className={cn('text-sm mt-2', isDarkMode ? 'text-gray-400' : 'text-gray-500')}>
                                    {hasImages
                                        ? tCanvas('no_image_selected_sub', { defaultValue: 'Pick an image from the sidebar.' })
                                        : tCanvas('no_images_yet_sub', { defaultValue: 'Upload your first batch of images to start drawing bounding boxes.' })}
                                </p>
                                {!hasImages && (
                                    <div className="mt-6">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            leftIcon={<Plus className="h-4 w-4" />}
                                            onClick={() => setUploadOpen(true)}
                                        >
                                            {tCanvas('upload_images', { defaultValue: 'Upload images' })}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </main>

                {/* Right-edge tab — visible at ALL widths when the right
                    rail is closed. Wide-screen users can collapse the
                    rail to reclaim canvas room and reopen it from here. */}
                {!rightRailOpen && (
                    <button
                        type="button"
                        onClick={openRight}
                        title={tCommon('open_right_rail', { defaultValue: 'Open classes panel' })}
                        aria-label={tCommon('open_right_rail', { defaultValue: 'Open classes panel' })}
                        className={cn(
                            'absolute right-0 top-3 z-30 w-8 h-10 rounded-l-md border border-r-0 shadow-sm flex items-center justify-center transition-colors',
                            isDarkMode
                                ? 'bg-gray-800 border-gray-700 text-emerald-400 hover:bg-gray-700'
                                : 'bg-white border-gray-300 text-emerald-600 hover:bg-gray-50',
                        )}
                    >
                        <PanelRightOpen className="h-4 w-4" />
                    </button>
                )}

                <div
                    className={cn(
                        'flex flex-col transition-all duration-200 overflow-hidden',
                        // Desktop: docked, in-flow. Width collapses to 0
                        // when closed so the canvas reclaims the space.
                        'lg:relative lg:translate-x-0 lg:z-auto lg:shrink-0',
                        rightRailOpen ? 'lg:w-80' : 'lg:w-0',
                        // Small screens: full-height absolute drawer
                        'absolute inset-y-0 right-0 z-30 w-[85vw] max-w-sm',
                        rightRailOpen ? 'translate-x-0' : 'translate-x-full',
                    )}
                >
                <RightRail
                    onCollapse={() => setRightRailOpen(false)}
                    isDarkMode={isDarkMode}
                    classes={state.classes}
                    boxes={state.boxes}
                    activeClassIndex={state.activeClassIndex}
                    selectedBox={selectedBox}
                    activeImage={state.activeImage}
                    orgId={orgId}
                    naturalSize={naturalSize}
                    hiddenClassIndices={hiddenClassIndices}
                    drawPriorityMode={drawPriorityMode}
                    isLocked={isLocked}
                    onRenameImage={isLocked ? undefined : (img) => setRenameTarget(img)}
                    view={rightRailView}
                    onViewChange={setRightRailView}
                    onSelectActive={state.setActiveClassIndex}
                    onToggleClassVisibility={toggleClassVisibility}
                    onAddClassClick={() => setAddClassOpen(true)}
                    onDeleteClassClick={(cls, inUseCount) => setDeleteClass({ cls, inUseCount })}
                    onEditClassClick={(cls) => setEditClass(cls)}
                    onSelectBoxOnly={(id) => {
                        // Row click in My Canvas → highlight box on canvas; do
                        // NOT auto-switch to Properties.
                        state.setSelectedBoxId(id);
                        setDrawPriorityMode(false);
                    }}
                    onOpenBoxInProperties={openBoxInProperties}
                    onChangeBox={(id, patch) => state.updateBox(id, patch)}
                    onDeleteBox={(id) => state.deleteBox(id)}
                    onToggleDrawPriority={toggleDrawPriority}
                    onBringBoxForward={(id) => state.bringBoxForward(id)}
                    onSendBoxBackward={(id) => state.sendBoxBackward(id)}
                    onContextMenuBox={(id, x, y) => {
                        // Right-click on a My Canvas row → set selection for
                        // canvas highlight + show menu; do NOT switch view.
                        state.setSelectedBoxId(id);
                        setContextMenu({ boxId: id, x, y });
                    }}
                />
                </div>
            </div>

            {/* Right-click context menu — fixed-position overlay */}
            {contextMenu && contextMenuBox && (
                <BoxContextMenu
                    isDarkMode={isDarkMode}
                    box={contextMenuBox}
                    classes={state.classes}
                    x={contextMenu.x}
                    y={contextMenu.y}
                    onClose={() => setContextMenu(null)}
                    onDelete={() => state.deleteBox(contextMenuBox.id)}
                    onChangeClass={(idx) => state.updateBox(contextMenuBox.id, { class_index: idx })}
                    onOpenInProperties={() => openBoxInProperties(contextMenuBox.id)}
                    onBringForward={() => state.bringBoxForward(contextMenuBox.id)}
                    onSendBackward={() => state.sendBoxBackward(contextMenuBox.id)}
                    onBringToFront={() => state.bringBoxToFront(contextMenuBox.id)}
                    onSendToBack={() => state.sendBoxToBack(contextMenuBox.id)}
                />
            )}

            <DeleteClassModal
                open={!!deleteClass}
                isDarkMode={isDarkMode}
                cls={deleteClass?.cls ?? null}
                inUseCount={deleteClass?.inUseCount ?? 0}
                projectId={projectId ?? ''}
                orgId={orgId}
                onClose={() => setDeleteClass(null)}
                onDeleted={(classIndex) => {
                    state.setClasses((prev) => prev.filter((c) => c.class_index !== classIndex));
                }}
            />

            <AboutModal
                open={aboutOpen}
                onClose={() => setAboutOpen(false)}
                onOpenLicense={() => { setAboutOpen(false); setLicenseOpen(true); }}
                isDarkMode={isDarkMode}
            />
            <LicenseModal
                open={licenseOpen}
                onClose={() => setLicenseOpen(false)}
                orgId={orgId}
                isDarkMode={isDarkMode}
            />
            <AddClassModal
                open={addClassOpen}
                onClose={() => setAddClassOpen(false)}
                orgId={orgId}
                projectId={projectId ?? ''}
                existingClasses={state.classes}
                isDarkMode={isDarkMode}
                onCreated={(cls) => {
                    state.setClasses((prev) => [...prev, cls].sort((a, b) => a.class_index - b.class_index));
                    state.setActiveClassIndex(cls.class_index);
                }}
            />
            <ClassPickerModal
                open={classFilterModalOpen}
                isDarkMode={isDarkMode}
                classes={state.classes}
                activeImageBoxes={state.boxes}
                onClose={() => setClassFilterModalOpen(false)}
                onPick={(idx) => state.setClassFilterExclusive(idx)}
                onClear={() => state.setClassFilterExclusive(null)}
            />
            <ExportDatasetModal
                open={exportModalOpen}
                isDarkMode={isDarkMode}
                imageCount={state.images.length}
                classCount={state.classes.length}
                onClose={() => setExportModalOpen(false)}
                onConfirm={(format, container) => void downloadSnapshot(format, container)}
                snapshotId={lockedSnapshotId}
                orgId={orgId}
                projectName={state.project?.name}
            />
            <LockAndSnapshotModal
                open={lockModalOpen}
                isDarkMode={isDarkMode}
                project={state.project}
                imageCount={state.images.length}
                classCount={state.classes.length}
                pendingImageCount={pendingImageCount}
                orgId={orgId}
                onClose={() => setLockModalOpen(false)}
                onResult={(p, snap, sum) => {
                    state.setProject(p);
                    // G.6 — capture split_summary so banner + chips render
                    // immediately. On unlock sum is undefined; effect above
                    // clears the summary when isLocked flips to false.
                    if (sum) setSplitSummary(sum);
                    // G.6 — also capture the new snapshot id so the
                    // Export modal can fetch /split-summary without an
                    // extra round-trip through snapshots.list.
                    if (snap) setLockedSnapshotId(snap.id);
                    // Refetch images so per-image .split fields land —
                    // they're populated server-side via JOIN to the latest
                    // snapshot's per-image splits.
                    if (p.is_locked && projectId && orgId) {
                        void imagesApi
                            .list(projectId, { status: undefined, classIndex: undefined }, orgId)
                            .then(state.setImages)
                            .catch(() => { /* non-fatal — next nav will reload */ });
                    } else if (!p.is_locked && projectId && orgId) {
                        // Also refetch on unlock so split chips disappear.
                        void imagesApi
                            .list(projectId, { status: undefined, classIndex: undefined }, orgId)
                            .then(state.setImages)
                            .catch(() => {});
                    }
                }}
            />
            <SplitDetailsModal
                open={splitDetailsOpen}
                summary={splitSummary}
                isDarkMode={isDarkMode}
                onClose={() => setSplitDetailsOpen(false)}
            />
            <RenameImageModal
                open={renameTarget !== null}
                image={renameTarget}
                orgId={orgId}
                isDarkMode={isDarkMode}
                onClose={() => setRenameTarget(null)}
                onRenamed={(updated) => {
                    // Patch the image in state.images so the canvas
                    // overlay + sidebar row both re-render with the
                    // new filename.
                    state.setImages((prev) => prev.map((i) =>
                        i.id === updated.id ? updated : i,
                    ));
                }}
            />
            <DownloadImagesModal
                open={!!downloadTarget}
                isDarkMode={isDarkMode}
                images={downloadTarget ?? []}
                classes={state.classes}
                projectId={projectId ?? ''}
                orgId={orgId}
                onClose={() => setDownloadTarget(null)}
            />
            <BulkDeleteImagesModal
                open={bulkDeleteIds.length > 0}
                isDarkMode={isDarkMode}
                projectId={projectId ?? ''}
                ids={bulkDeleteIds}
                images={state.images}
                orgId={orgId}
                onClose={() => setBulkDeleteIds([])}
                onDeleted={(removedIds) => {
                    const removed = new Set(removedIds);
                    state.setImages((prev) => prev.filter((i) => !removed.has(i.id)));
                    if (state.activeImageId && removed.has(state.activeImageId)) {
                        const remaining = state.images.filter((i) => !removed.has(i.id));
                        state.setActiveImageId(remaining.length > 0 ? remaining[0].id : null);
                    }
                }}
            />
            <DeleteImageModal
                open={!!deleteImage}
                isDarkMode={isDarkMode}
                image={deleteImage}
                // The box count is reliable only for the currently active
                // image (state.boxes is per-image). For other images we
                // fall back to 0 — server still cascade-deletes anyway.
                boxCount={deleteImage && deleteImage.id === state.activeImageId ? state.boxes.length : 0}
                orgId={orgId}
                onClose={() => setDeleteImage(null)}
                onDeleted={(deletedId) => {
                    state.setImages((prev) => prev.filter((i) => i.id !== deletedId));
                    if (state.activeImageId === deletedId) {
                        // Pick another image if one exists; otherwise clear.
                        const remaining = state.images.filter((i) => i.id !== deletedId);
                        state.setActiveImageId(remaining.length > 0 ? remaining[0].id : null);
                    }
                }}
            />
            <EditClassModal
                open={!!editClass}
                isDarkMode={isDarkMode}
                projectId={projectId ?? ''}
                cls={editClass}
                existingClasses={state.classes}
                orgId={orgId}
                onClose={() => setEditClass(null)}
                onUpdated={(updated) => {
                    state.setClasses((prev) =>
                        prev.map((c) => (c.class_index === updated.class_index ? updated : c)),
                    );
                }}
            />
            <ImageUploadModal
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                orgId={orgId}
                projectId={projectId ?? ''}
                isDarkMode={isDarkMode}
                maxImageSizeMB={orgConfig?.max_image_size_mb}
                onUploaded={(meta) => {
                    state.setImages((prev) => {
                        if (prev.some((p) => p.id === meta.id)) return prev;
                        return [...prev, meta];
                    });
                    if (!state.activeImageId) state.setActiveImageId(meta.id);
                }}
            />
        </div>
    );
}
